import { MKT_PRESETS, type MktPreset } from '@portal/contracts';
import type { Me, MktDelta, MktDimension, MktQuelle, MktQuelleStatus, MktTagKennzahl, MktTagWert, ModulFreischaltung } from '@portal/contracts';

/**
 * Modul „Marketing“ (#865c, Konzept v2 Stufe 1): reine Hilfsfunktionen der Cockpit-Seite — getestet ohne DOM. Rechte prüft
 * immer die API (Modul-Gate + Cerbos `marketing`); die Oberfläche blendet nur aus.
 */
export const MARKETING_PFAD = '/marketing';

/** #895g Zeitraum der Seite: Preset (7 T / 30 T / Quartal / Jahr / gesamt) oder frei Von/Bis — Vergleiche rechnet die API */
export const ZEITRAEUME = MKT_PRESETS;
export type Zeitraum = { preset: MktPreset } | { von: string; bis: string };
export const zeitraumQuery = (z: Zeitraum): Record<string, string> => ('preset' in z ? { preset: z.preset } : { von: z.von, bis: z.bis });
/** Von/Bis aus TT.MM.JJJJ prüfen: gültig, von ≤ bis, bis ≤ gestern; Fehlertext oder Zeitraum */
export function freierZeitraum(vonText: string, bisText: string, gestern: string): Zeitraum | string {
  const von = tagAusDeutsch(vonText);
  const bis = tagAusDeutsch(bisText);
  if (!von || !bis) return 'Bitte Von und Bis als TT.MM.JJJJ eingeben.';
  if (bis > gestern) return `Bis spätestens ${tagDeutsch(gestern)} (gestern).`;
  if (von > bis) return 'Von muss vor oder gleich Bis liegen.';
  return { von, bis };
}

/** Panels P1–P7 (Konzept v2 § 1) in Seitenreihenfolge */
export const PANELS = [
  { key: 'p1', titel: 'Tagesübersicht' },
  { key: 'p2', titel: 'Herkunft Sessions' },
  { key: 'p3', titel: 'Herkunft Bestellungen & Umsatz' },
  { key: 'p4', titel: 'KI-Assistenten' },
  { key: 'p5', titel: 'SEO-Rankings' },
  { key: 'p6', titel: 'SEO-Chancen (Position 4–20)' },
  { key: 'p7', titel: 'SEO-Wettbewerber' },
] as const;

const ZAHL = new Intl.NumberFormat('de-DE');
const EURO = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const PROZENT = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1, minimumFractionDigits: 1 });
const DATUM = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin' });
const TAG = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' });

export const zahl = (v: number | null | undefined) => (v === null || v === undefined ? '—' : ZAHL.format(v));
export const euro = (v: number | null | undefined) => (v === null || v === undefined ? '—' : EURO.format(v));
export const prozent = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${PROZENT.format(v)} %`);
export const anteil = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${PROZENT.format(v * 100)} %`);
export const tagKurz = (tag: string) => TAG.format(new Date(`${tag}T00:00:00Z`));

/** Δ-Anzeige: Richtung (für Farbe + Pfeil) und Text mit Vorzeichen; Bildschirmleser bekommen den Vergleich ausgeschrieben */
export function deltaAnzeige(d: number | null, vergleich: 'Vortag' | 'Vorwoche' | 'Vormonat' | 'Vorjahr'): { richtung: 'hoch' | 'runter' | 'gleich' | 'leer'; text: string; sr: string } {
  if (d === null) return { richtung: 'leer', text: '—', sr: `kein Vergleichswert ${vergleich}` };
  const richtung = d > 0 ? 'hoch' : d < 0 ? 'runter' : 'gleich';
  const text = `${d > 0 ? '+' : d < 0 ? '−' : '±'}${PROZENT.format(Math.abs(d))} %`;
  const wort = d > 0 ? 'mehr' : d < 0 ? 'weniger' : 'unverändert';
  return { richtung, text, sr: d === 0 ? `unverändert gegenüber ${vergleich}` : `${PROZENT.format(Math.abs(d))} Prozent ${wort} als ${vergleich}` };
}
export const deltas = (d: MktDelta) => [deltaAnzeige(d.vorwoche, 'Vorwoche'), deltaAnzeige(d.vormonat, 'Vormonat'), deltaAnzeige(d.vorjahr ?? null, 'Vorjahr')] as const;

/** Datenstand-Hinweis je Panel: ohne Lieferung ausdrücklich „noch keine Daten“, nie eine leere Zahl */
export function datenstand(stand: string | null): string {
  if (!stand) return 'Datenstand: noch keine Daten angebunden';
  const d = new Date(stand);
  return Number.isNaN(d.getTime()) ? 'Datenstand: unbekannt' : `Datenstand: ${DATUM.format(d)}`;
}

/** Anzeige leerer Dimensionen */
export const dimText = (v: string, leer = '(ohne)') => (v ? v : leer);

export interface Knoten { schluessel: string; label: string; wert: number; vorwoche: number; vormonat: number; vorjahr: number; kinder: Knoten[] }

/**
 * Drilldown P2/P3: Kanalgruppe → Referrer-Domain → utm source/medium → campaign · content. Summen je Ebene; Kinder absteigend.
 * `wert`/`vw`/`vm` liefern die Kennzahl einer Zeile (Sessions bzw. Umsatz).
 */
export function baum<T extends MktDimension>(zeilen: readonly T[], wert: (z: T) => number, vw: (z: T) => number, vm: (z: T) => number, vj: (z: T) => number = () => 0): Knoten[] {
  const ebenen: ((z: T) => string)[] = [
    (z) => dimText(z.kanalgruppe, '(ohne Kanal)'),
    (z) => dimText(z.referrerName, '(ohne Referrer)'),
    (z) => (z.utmSource || z.utmMedium ? `${dimText(z.utmSource)} / ${dimText(z.utmMedium)}` : '(ohne UTM)'),
    (z) => (z.utmCampaign || z.utmContent ? `${dimText(z.utmCampaign)} · ${dimText(z.utmContent)}` : '(ohne Kampagne)'),
  ];
  const bauen = (zs: readonly T[], tiefe: number, pfad: string): Knoten[] => {
    if (tiefe >= ebenen.length) return [];
    const gruppen = new Map<string, T[]>();
    for (const z of zs) {
      const k = ebenen[tiefe]!(z);
      gruppen.set(k, [...(gruppen.get(k) ?? []), z]);
    }
    const knoten = [...gruppen.entries()].map(([label, g]) => {
      const schluessel = `${pfad}/${label}`;
      const kinder = bauen(g, tiefe + 1, schluessel);
      // eine einzige „ohne“-Unterebene ohne Mehrwert weglassen
      const sinnvoll = kinder.length === 1 && kinder[0]!.label.startsWith('(ohne') && kinder[0]!.kinder.length === 0 ? [] : kinder;
      return { schluessel, label, wert: sum(g, wert), vorwoche: sum(g, vw), vormonat: sum(g, vm), vorjahr: sum(g, vj), kinder: sinnvoll };
    });
    return knoten.sort((a, b) => b.wert - a.wert || a.label.localeCompare(b.label, 'de'));
  };
  return bauen(zeilen, 0, '');
}
const sum = <T>(zs: readonly T[], f: (z: T) => number) => Math.round(zs.reduce((a, z) => a + f(z), 0) * 100) / 100;

/** Δ % wie die API (gerundet auf 0,1; null ohne Vergleichswert) — für Baumknoten */
export const deltaProzent = (ist: number, vor: number): number | null => (vor === 0 ? null : Math.round(((ist - vor) / vor) * 1000) / 10);

/** SEO-Positionsänderung: „▲ 8“ besser, „▼ 1“ schlechter, „neu“ */
export function positionDelta(delta: number | null, status: 'besser' | 'schlechter' | 'gleich' | 'neu'): { text: string; sr: string; richtung: 'hoch' | 'runter' | 'gleich' | 'leer' } {
  if (status === 'neu' || delta === null) return { text: 'neu', sr: 'neu in der Liste', richtung: 'leer' };
  if (delta === 0) return { text: '±0', sr: 'Position unverändert', richtung: 'gleich' };
  return delta > 0
    ? { text: `▲ ${delta}`, sr: `${delta} Positionen besser`, richtung: 'hoch' }
    : { text: `▼ ${-delta}`, sr: `${-delta} Positionen schlechter`, richtung: 'runter' };
}

/**
 * SEO-URL für die Anzeige: Pfad (+ Query) einer absoluten oder relativen URL. Der Inbox-Vertrag erlaubt beliebige Strings —
 * nie werfen; Unparsbares wird roh (gekürzt) gezeigt, leer → „—“.
 */
export function seoPfad(url: string | null | undefined): string {
  const roh = (url ?? '').trim();
  if (!roh) return '—';
  try {
    const u = new URL(roh, 'https://www.parfuemerie-brueckner.com');
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return roh.slice(0, 200);
    return `${u.pathname}${u.search}` || '/';
  } catch {
    return roh.slice(0, 200);
  }
}

// ── #895 Tagesauswertung (P1 oben) ─────────────────────────────────────────────────────────────────────────────────
export const TAGESAUSWERTUNG_TITEL = 'Tagesauswertung';

/** Standard-Tag der Tagesauswertung: gestern (Europe/Berlin), YYYY-MM-DD */
export function gesternBerlin(jetzt = new Date()): string {
  const heute = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(jetzt);
  return new Date(Date.parse(`${heute}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
}

/** #895d: Nachlauf 3 Tage — Tage bis T+3 (heute − 3 … gestern) können sich noch ändern (Attribution/Stornos) */
export const NACHLAUF_TAGE = 3;
export function istVorlaeufig(tag: string, jetzt = new Date()): boolean {
  const heute = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(jetzt);
  return Date.parse(`${tag}T00:00:00Z`) >= Date.parse(`${heute}T00:00:00Z`) - NACHLAUF_TAGE * 86_400_000;
}
export const VORLAEUFIG_HINWEIS = 'Vorläufig: Werte der letzten 3 Tage werden beim nächsten Lauf noch nachgeführt.';

/** #895d Datumsfeld deutsch, unabhängig von der Browser-Sprache: ISO JJJJ-MM-TT ↔ TT.MM.JJJJ */
export const tagDeutsch = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
export function tagAusDeutsch(text: string): string | null {
  const m = /^\s*(\d{1,2})\.(\d{1,2})\.(\d{4})\s*$/.exec(text);
  if (!m) return null;
  const [, tt = '', mm = '', jj = ''] = m;
  const iso = `${jj}-${mm.padStart(2, '0')}-${tt.padStart(2, '0')}`;
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== iso ? null : iso;
}

const ZAHL2 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const faktor = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${ZAHL2.format(v)} ×`);

/**
 * Kacheln der Tagesauswertung in Anzeigereihenfolge; `paar` = nebeneinander (Sessions Shopify | GA4), `zusatz` = Nebenwert in
 * derselben Kachel (ROAS + MER unter den Ads-Kosten).
 */
export const TAG_KACHELN: readonly { key: MktTagKennzahl; label: string; format: (v: number | null) => string; zusatz?: { key: MktTagKennzahl; label: string; format: (v: number | null) => string }[]; paar?: true }[] = [
  { key: 'sessionsShopify', label: 'Sessions Shopify', format: zahl, paar: true },
  { key: 'sessionsGa4', label: 'Sessions GA4', format: zahl, paar: true },
  { key: 'orders', label: 'Bestellungen', format: zahl },
  { key: 'umsatz', label: 'Umsatz', format: euro },
  { key: 'bestellwert', label: 'Ø Bestellwert', format: euro },
  { key: 'neueKunden', label: 'Neue Kunden', format: zahl },
  { key: 'adsKosten', label: 'Ads-Kosten', format: euro, zusatz: [{ key: 'roas', label: 'ROAS', format: faktor }, { key: 'mer', label: 'MER', format: faktor }] },
  { key: 'kiSessions', label: 'KI-Assistenten-Sessions', format: zahl },
];

/** Δ Vortag / Vorwoche / Vormonat eines Tageswerts */
export const tagDeltas = (w: MktTagWert) => [deltaAnzeige(w.delta.vortag, 'Vortag'), deltaAnzeige(w.delta.vorwoche, 'Vorwoche'), deltaAnzeige(w.delta.vormonat, 'Vormonat'), deltaAnzeige(w.delta.vorjahr ?? null, 'Vorjahr')] as const;

/** Status-Badge je Quelle: Text + Ton (ok = grün, warn = Warnfarbe, aus = gedämpft) — nur bestehende Portal-Tokens */
export const QUELLE_STATUS: Record<MktQuelleStatus, { text: string; ton: 'ok' | 'warn' | 'aus' }> = {
  aktuell: { text: 'aktuell', ton: 'ok' },
  veraltet: { text: 'veraltet', ton: 'warn' },
  fehler: { text: 'Fehler', ton: 'warn' },
  leer: { text: 'noch keine Daten', ton: 'aus' },
  wartet_freigabe: { text: 'wartet auf Freigabe', ton: 'aus' },
  inaktiv: { text: 'inaktiv', ton: 'aus' },
};

/** Zeile unter dem Quellennamen: Hinweis/Fehler vor Datenstand; nie leer */
export function quelleText(q: MktQuelle): string {
  if (q.status === 'fehler') return q.lastError ? `Letzter Lauf fehlgeschlagen: ${q.lastError}` : 'Letzter Lauf fehlgeschlagen.';
  if (q.hinweis) return q.hinweis;
  const teile = [q.letzterTag ? `Daten bis ${tagKurz(q.letzterTag)}` : null, q.datenstand ? datenstand(q.datenstand) : null].filter(Boolean);
  return teile.length ? teile.join(' · ') : 'Noch keine Lieferung.';
}

/** Nur `/marketing` gehört zu dieser Seite; Unterpfade (z. B. /marketing/benachrichtigungen) sind eigene Module */
/** #865f P4: KI-Panel ohne Datenstand zeigt Leermeldung statt Nullwert-Tabelle */
export const KI_LEER = 'Noch keine KI-Daten.';
export const kiHatDaten = (d: { datenstand: string | null }): boolean => d.datenstand !== null;

/** #865f P3: dauerhafte Erklärung „Ohne Journey“ — unabhängig vom Datenstand, ohne Messwerte */
export const OHNE_JOURNEY_HINWEIS = 'Ohne Journey (nicht zuordenbar): Bestellungen ohne Besuchsdaten (Einwilligung/Cookie) werden als eigene Zeile ausgewiesen — keine Hochrechnung.';

export const istMarketingSeite = (pathname: string): boolean => (pathname.replace(/\/+$/, '') || '/') === MARKETING_PFAD;

type MitModulen = Pick<Me, 'modules'> | { modules?: ModulFreischaltung };
export const darfVerwalten = (me: MitModulen): boolean => me.modules?.marketing === 'verwalten';
